import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Hidden line property carrying the discount code a quantity tier was bought
 * with, and the cart attribute listing the codes applied that way.
 *
 * Between them the cart is self-describing: `assets/mm-cart.js` can tell a code
 * this section applied from one the shopper or the rewards bar added, and drop
 * it once no line is carrying it any more. Both names are a contract with
 * `reconcileTierDiscounts` there — change them in both places or not at all.
 */
export const TIER_DISCOUNT_PROPERTY = '_mmx_tier_discount';
export const TIER_DISCOUNT_ATTRIBUTE = '_mmx_tier_discounts';

/**
 * Gallery, variant picker, quantity tiers, Subscribe & Save and add-to-cart for
 * `sections/mmx-product-information.liquid`.
 *
 * The artboard's quantity rows are not variants — every row buys the same
 * variant, only more of it — so this is not a native product form. The CTA posts
 * one `{ items: [...] }` payload to `/cart/add.js`, mirroring
 * `ProductFormComponent#processBatchAddToCart` in `product-form.js`, then applies
 * the optional discount code through `/cart/update.js` the way `cart-discount.js`
 * does. That ordering matters: the code has to land after the lines exist,
 * otherwise a quantity-gated discount is rejected as inapplicable.
 */
class MmxProductInformation extends HTMLElement {
  /** @type {boolean} */
  #submitting = false;

  /** @type {number | undefined} */
  #addedTimeout;

  /**
   * Each tier's parsed `data-fixed-prices`, keyed by its element so a theme
   * editor re-render (new elements) never reads a stale map.
   * @type {WeakMap<HTMLElement, Record<string, number>>}
   */
  #fixedPrices = new WeakMap();

  /**
   * Listeners are bound per connection: a section re-render in the theme editor
   * moves the element rather than replacing it, so `connectedCallback` runs again
   * and un-scoped listeners would stack (each Add click counting twice, and so on).
   * @type {AbortController}
   */
  #listeners = new AbortController();

  /** @type {IntersectionObserver | undefined} */
  #floatObserver;

  connectedCallback() {
    this.#listeners = new AbortController();

    this.variants = this.#readVariants();
    this.currentVariant =
      this.variants.find((variant) => String(variant.id) === this.dataset.variantId) ?? this.variants[0] ?? null;

    this.#setupGallery();
    this.#setupOptions();
    this.#setupSubscription();
    this.#setupTiers();
    this.#setupBuyButtons();
    this.#setupFloatingButton();
  }

  disconnectedCallback() {
    this.#listeners.abort();
    this.#floatObserver?.disconnect();
    if (this.#addedTimeout) clearTimeout(this.#addedTimeout);
  }

  #readVariants() {
    const node = this.querySelector('[data-mmx-pi-variants]');
    if (!node?.textContent) return [];

    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /* ------------------------------- Gallery ------------------------------- */

  #setupGallery() {
    this.slides = Array.from(this.querySelectorAll('[data-mmx-pi-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-mmx-pi-thumb]'));

    if (this.slides.length <= 1) return;

    const { signal } = this.#listeners;

    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.#goToMedia(index), { signal });
    });

    // The server opens on the variant's image, which can sit past the three
    // visible thumbs.
    const active = this.thumbs.find((thumb) => thumb.classList.contains('is-active'));
    if (active) this.#revealThumb(active);
  }

  /**
   * Jumps to the variant's featured image, the client-side half of what
   * Horizon's gallery gets from re-rendering on `?variant=`. A variant with no
   * image of its own leaves the gallery where the shopper put it.
   * @param {{ featured_media_id?: number | null } | null} variant
   */
  #showVariantMedia(variant) {
    const mediaId = variant?.featured_media_id;
    if (!mediaId || !this.slides?.length) return;

    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index !== -1) this.#goToMedia(index);
  }

  /**
   * Scrolls the strip, not the page, so the thumb is visible. `scrollIntoView`
   * would also scroll the window on mobile, where the strip sits under the fold.
   * @param {HTMLElement} thumb
   */
  #revealThumb(thumb) {
    const strip = thumb.parentElement;
    if (!strip) return;

    const stripRect = strip.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    if (thumbRect.left >= stripRect.left && thumbRect.right <= stripRect.right) return;

    strip.scrollTo({ left: strip.scrollLeft + thumbRect.left - stripRect.left, behavior: 'smooth' });
  }

  /** @param {number} index */
  #goToMedia(index) {
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.thumbs.forEach((thumb, thumbIndex) => {
      const active = thumbIndex === index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) this.#revealThumb(thumb);
    });
  }

  /* --------------------------- Variant picker ---------------------------- */

  #setupOptions() {
    this.optionInputs = Array.from(this.querySelectorAll('[data-mmx-pi-option-value]'));
    this.variantInput = this.querySelector('[data-mmx-pi-variant-input]');

    if (this.optionInputs.length === 0) return;

    const { signal } = this.#listeners;

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this.#selectVariantFromOptions(), { signal });
    });
  }

  #selectVariantFromOptions() {
    const selected = this.optionInputs.filter((input) => input.checked).sort((a, b) => {
      return Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition);
    });

    const chosen = selected.map((input) => input.value);
    const match = this.variants.find((variant) => {
      return (
        Array.isArray(variant.options) &&
        variant.options.length === chosen.length &&
        variant.options.every((value, index) => value === chosen[index])
      );
    });

    if (!match) return;

    this.currentVariant = match;
    this.dataset.variantId = String(match.id);
    this.dataset.variantPrice = String(match.price);
    if (this.variantInput instanceof HTMLInputElement) this.variantInput.value = String(match.id);

    // Keep the address bar shareable without a reload; `?variant=` is what the
    // rest of the theme (and Shopify's own analytics) reads.
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(match.id));
    window.history.replaceState({}, '', url);

    this.#showVariantMedia(match);
    this.#syncSubscription();
    this.#syncTierVisibility();
    this.#repriceTiers();
    this.#updateBuyState();
  }

  /* --------------------------- Subscribe & Save --------------------------- */

  /**
   * The Subscribe & Save row (blocks/mmx-pi-subscription). Its delivery pills are
   * the product's selling plans; which of them the current variant can use, and
   * at what price, comes from that variant's `selling_plan_allocations`.
   */
  #setupSubscription() {
    this.subscription = this.querySelector('[data-mmx-pi-subscription]');
    if (!(this.subscription instanceof HTMLElement)) return;

    this.subscribeInput = this.subscription.querySelector('[data-mmx-pi-subscribe]');
    this.subscriptionPanel = this.subscription.querySelector('[data-mmx-pi-subscription-panel]');
    this.subscriptionLabel = this.subscription.querySelector('[data-mmx-pi-subscription-label]');
    this.planInputs = Array.from(this.subscription.querySelectorAll('[data-mmx-pi-plan]'));

    const { signal } = this.#listeners;

    this.subscribeInput?.addEventListener('change', () => this.#onSubscriptionChange(), { signal });
    this.planInputs.forEach((input) => {
      input.addEventListener('change', () => this.#onSubscriptionChange(), { signal });
    });

    this.#syncSubscription();
  }

  #onSubscriptionChange() {
    this.#renderSubscriptionState();
    this.#repriceTiers();
    this.#writeSellingPlanToUrl();
  }

  /**
   * Re-fits the row to the current variant: hidden when it has no plans, pills it
   * cannot use disabled, and the selection moved off a plan it cannot use.
   */
  #syncSubscription() {
    if (!(this.subscription instanceof HTMLElement)) return;

    const allocations = this.#allocations;
    const eligible = allocations.length > 0;
    this.subscription.hidden = !eligible;

    const allowed = new Set(allocations.map((allocation) => String(allocation.selling_plan_id)));
    this.planInputs.forEach((input) => {
      const usable = allowed.has(input.value);
      input.disabled = !usable;
      input.nextElementSibling?.classList.toggle('is-unavailable', !usable);
      if (!usable) input.checked = false;
    });

    if (eligible && !this.planInputs.some((input) => input.checked)) {
      const first = this.planInputs.find((input) => !input.disabled);
      if (first) first.checked = true;
    }

    if (this.subscribeInput instanceof HTMLInputElement) {
      // A subscription-only product has nothing else to offer: keep it ticked.
      if (this.subscription.hasAttribute('data-requires-plan')) this.subscribeInput.checked = eligible;
      if (!eligible) this.subscribeInput.checked = false;
    }

    this.#updateSubscriptionLabel();
    this.#renderSubscriptionState();
    this.#writeSellingPlanToUrl();
  }

  #renderSubscriptionState() {
    const open = this.#isSubscribed;
    if (this.subscriptionPanel instanceof HTMLElement) this.subscriptionPanel.hidden = !open;
    this.subscribeInput?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /** "Subscribe & Save [savings]%" at the variant's best plan saving. */
  #updateSubscriptionLabel() {
    if (!(this.subscriptionLabel instanceof HTMLElement) || !(this.subscription instanceof HTMLElement)) return;

    const best = this.#allocations.reduce((max, allocation) => {
      return Math.max(max, this.#savingsPercent(allocation));
    }, 0);

    const template = this.subscription.dataset.label || '';
    const fallback = this.subscription.dataset.labelNoSavings || template.replace(/\s*\[savings\]%?/g, '').trim();
    const label = best > 0 && template ? template.replace('[savings]', String(best)) : fallback;
    if (label) this.subscriptionLabel.textContent = label;
  }

  /** @param {{ price: number, compare_at_price: number }} allocation */
  #savingsPercent(allocation) {
    const compare = Number(allocation.compare_at_price) || 0;
    const price = Number(allocation.price) || 0;
    if (compare <= 0 || price >= compare) return 0;
    return Math.round(((compare - price) / compare) * 100);
  }

  /** @returns {{ selling_plan_id: number, price: number, compare_at_price: number }[]} */
  get #allocations() {
    const allocations = this.currentVariant?.selling_plan_allocations;
    return Array.isArray(allocations) ? allocations : [];
  }

  get #isSubscribed() {
    return (
      this.subscription instanceof HTMLElement &&
      !this.subscription.hidden &&
      this.subscribeInput instanceof HTMLInputElement &&
      this.subscribeInput.checked &&
      this.#selectedAllocation !== null
    );
  }

  /** The chosen plan's allocation for the current variant, whether or not the box is ticked. */
  get #selectedAllocation() {
    const planId = this.planInputs?.find((input) => input.checked && !input.disabled)?.value;
    if (!planId) return null;
    return this.#allocations.find((allocation) => String(allocation.selling_plan_id) === planId) ?? null;
  }

  get #sellingPlanId() {
    return this.#isSubscribed ? Number(this.#selectedAllocation?.selling_plan_id) || null : null;
  }

  /** `?selling_plan=` is what Shopify reads to reopen the page on the same plan. */
  #writeSellingPlanToUrl() {
    if (!this.subscription) return;

    const url = new URL(window.location.href);
    const planId = this.#sellingPlanId;
    if (planId) {
      url.searchParams.set('selling_plan', String(planId));
    } else {
      url.searchParams.delete('selling_plan');
    }
    if (url.href !== window.location.href) window.history.replaceState({}, '', url);
  }

  /* ---------------------------- Quantity tiers ---------------------------- */

  #setupTiers() {
    this.tiers = Array.from(this.querySelectorAll('[data-mmx-pi-tier]'));
    if (this.tiers.length === 0) return;

    const { signal } = this.#listeners;

    this.tiers.forEach((tier) => {
      const input = tier.querySelector('input[type="radio"]');
      input?.addEventListener('change', () => this.#updateBuyState(), { signal });
    });

    this.quantityGroup = this.querySelector('[data-mmx-pi-quantity]');

    this.#syncTierVisibility();
    this.#repriceTiers();
  }

  /**
   * Shows each row only on the variants its "Show on" setting allows (the IDs
   * in `data-variant-ids`; no attribute means every variant), and keeps one
   * visible row selected: the shopper's choice while it is still shown, else the
   * merchant's default, else the first row — the CTA needs a quantity to send.
   * A variant with no rows at all hides the group and buys one.
   */
  #syncTierVisibility() {
    if (!this.tiers?.length) return;

    const variantId = String(this.currentVariant?.id ?? '');
    const visible = this.tiers.filter((tier) => {
      const ids = tier.dataset.variantIds;
      const shown = ids === undefined || ids.split(',').includes(variantId);
      tier.hidden = !shown;

      const input = tier.querySelector('input[type="radio"]');
      if (input instanceof HTMLInputElement) {
        input.disabled = !shown;
        if (!shown) input.checked = false;
      }
      return shown;
    });

    if (this.quantityGroup instanceof HTMLElement) this.quantityGroup.hidden = visible.length === 0;

    if (!visible.some((tier) => tier.querySelector('input[type="radio"]')?.checked)) {
      const fallback = visible.find((tier) => tier.hasAttribute('data-selected-by-default')) ?? visible[0];
      const input = fallback?.querySelector('input[type="radio"]');
      if (input instanceof HTMLInputElement) input.checked = true;
    }
  }

  #repriceTiers() {
    if (!this.tiers?.length || !this.currentVariant) return;

    // Subscribed, Shopify takes the tier discount off the plan price rather
    // than the one-time price, so the percentages compound (10% plan + 5% tier
    // is 14.5% off, not 15%). Price the rows the same way and strike them
    // through against the one-time price so the combined saving shows.
    const oneTimePrice = Number(this.currentVariant.price) || 0;
    const allocation = this.#isSubscribed ? this.#selectedAllocation : null;
    const unitPrice = allocation ? Number(allocation.price) || oneTimePrice : oneTimePrice;
    const planDiscount = allocation ? this.#savingsPercent(allocation) : 0;

    this.tiers.forEach((tier) => {
      const quantity = Number(tier.dataset.quantity) || 1;
      const discount = Number(tier.dataset.discount) || 0;
      const compare = oneTimePrice * quantity;
      const fixed = this.#fixedTierPrice(tier);

      let total;
      let savings;
      if (fixed > 0) {
        // A fixed total can only be backed by an amount-off discount, and on a
        // subscription line Shopify takes that amount off the plan price — so
        // the plan's saving per unit comes off the fixed total on top.
        total = Math.max(0, fixed - (oneTimePrice - unitPrice) * quantity);
        // Floored, so the badge never claims more than the row saves.
        savings = compare > total ? Math.floor(((compare - total) * 100) / compare) : 0;
      } else {
        // Shopify discounts one unit at a time, rounds that unit's discount DOWN
        // to the cent (so the unit price rounds up: $6.29 plan - 10% = $5.661 ->
        // $5.67), then multiplies out by quantity. Checked against the cart:
        // 3 x $6.29 @ 5% = $17.94, 6 x $6.29 @ 10% = $34.02.
        const unitDiscount = Math.floor((unitPrice * discount) / 100);
        total = (unitPrice - unitDiscount) * quantity;
        // The badge names the two offers added together (5% tier + 10% plan =
        // "Save 15%"), not the compounded saving the price above works out to.
        savings = discount + planDiscount;
      }

      const priceNode = tier.querySelector('[data-mmx-pi-tier-price]');
      if (priceNode) priceNode.textContent = this.#money(total);

      const compareNode = tier.querySelector('[data-mmx-pi-tier-compare]');
      if (compareNode) compareNode.textContent = total < compare ? this.#money(compare) : '';

      const badge = tier.querySelector('[data-mmx-pi-tier-badge]');
      if (badge instanceof HTMLElement) {
        badge.textContent = (badge.dataset.template || '')
          .replaceAll('[savings]', String(savings))
          .replaceAll('[amount]', this.#money(Math.max(0, compare - total)));
        badge.hidden = savings <= 0;
      }
    });
  }

  /**
   * The row's fixed total for the current variant in cents, or 0 when the
   * variant takes the percentage instead. The block has already matched its
   * "Fixed prices" lines to variant IDs (blocks/mmx-pi-quantity-tier.liquid).
   * @param {HTMLElement} tier
   */
  #fixedTierPrice(tier) {
    let prices = this.#fixedPrices.get(tier);
    if (!prices) {
      try {
        prices = JSON.parse(tier.dataset.fixedPrices || '{}') || {};
      } catch {
        prices = {};
      }
      this.#fixedPrices.set(tier, prices);
    }
    return Number(prices[String(this.currentVariant?.id)]) || 0;
  }

  get #selectedTier() {
    return this.tiers?.find((tier) => !tier.hidden && tier.querySelector('input[type="radio"]')?.checked) ?? null;
  }

  get #selectedQuantity() {
    return Number(this.#selectedTier?.dataset.quantity) || 1;
  }

  /**
   * The selected tier's own code, if it has one. Deliberately does NOT fall back
   * to the buy-buttons block's code: only a tier code is stamped onto its line
   * and tracked for removal, so conflating the two would make the block's
   * page-wide code vanish the moment any line left the cart.
   */
  get #selectedTierDiscountCode() {
    return (this.#selectedTier?.dataset.discountCode || '').trim();
  }

  /* ------------------------------ Buy buttons ----------------------------- */

  #setupBuyButtons() {
    this.buy = this.querySelector('[data-mmx-pi-buy]');
    this.cta = this.querySelector('[data-mmx-pi-atc]');
    this.ctaLabel = this.querySelector('[data-mmx-pi-atc-label]');
    this.errorNode = this.querySelector('[data-mmx-pi-error]');

    const { signal } = this.#listeners;

    this.cta?.addEventListener(
      'click',
      () => {
        if (!this.currentVariant) return;

        // The tier's code rides along on the line as a hidden property. That is
        // what lets the cart drawer drop the discount again when the line goes:
        // nothing else on the cart records which line an offer came in with.
        const tierCode = this.#selectedTierDiscountCode;
        /** @type {{ id: number, quantity: number, selling_plan?: number, properties?: Record<string, string> }} */
        const item = { id: this.currentVariant.id, quantity: this.#selectedQuantity };
        const sellingPlan = this.#sellingPlanId;
        if (sellingPlan) item.selling_plan = sellingPlan;
        if (tierCode) item.properties = { [TIER_DISCOUNT_PROPERTY]: tierCode };

        this.#addToCart([item], this.cta, tierCode);
      },
      { signal }
    );

    this.querySelectorAll('[data-mmx-pi-promo-add]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const id = Number(button.dataset.variantId);
          if (!id) return;
          this.#addToCart([{ id, quantity: 1 }], button);
        },
        { signal }
      );
    });

    this.#updateBuyState();
  }

  #updateBuyState() {
    if (!(this.cta instanceof HTMLButtonElement)) return;

    const available = this.currentVariant ? this.currentVariant.available !== false : false;

    this.cta.disabled = !available;
    this.cta.setAttribute('aria-disabled', available ? 'false' : 'true');

    if (this.ctaLabel && this.buy instanceof HTMLElement) {
      this.ctaLabel.textContent = available
        ? this.buy.dataset.defaultLabel || this.ctaLabel.textContent
        : this.buy.dataset.soldOutLabel || this.ctaLabel.textContent;
    }

    if (this.floatLabel && this.ctaLabel) this.floatLabel.textContent = this.ctaLabel.textContent;
  }

  /* ---------------------------- Floating button --------------------------- */

  /**
   * Mobile stand-in for the add to cart button (the stylesheet hides it from 750px).
   * It shows only once the whole section is above the viewport — while any of the
   * section is on screen, the sticky button inside it is doing the job — and a tap
   * scrolls back up to the section instead of adding to the cart.
   */
  #setupFloatingButton() {
    this.float = this.querySelector('[data-mmx-pi-float]');
    this.floatLabel = this.querySelector('[data-mmx-pi-float-label]');
    const button = this.querySelector('[data-mmx-pi-float-button]');

    if (!(this.float instanceof HTMLElement) || !this.cta || !this.ctaLabel) return;

    this.float.hidden = false;
    if (this.floatLabel) this.floatLabel.textContent = this.ctaLabel.textContent;

    const { signal } = this.#listeners;

    button?.addEventListener(
      'click',
      () => {
        const target = this.querySelector('.mmx-pi__anchor') ?? this;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      },
      { signal }
    );

    this.#floatObserver?.disconnect();
    this.#floatObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      this.float?.classList.toggle('is-visible', scrolledPast);
    });
    this.#floatObserver.observe(this);
  }

  /**
   * @param {{ id: number, quantity: number, selling_plan?: number, properties?: Record<string, string> }[]} items
   * @param {Element | null} trigger
   * @param {string} [tierCode] - the selected tier's code, already stamped on the line
   */
  async #addToCart(items, trigger, tierCode) {
    if (this.#submitting || items.length === 0) return;

    this.#submitting = true;
    this.#hideError();
    trigger?.setAttribute('aria-busy', 'true');
    if (trigger instanceof HTMLButtonElement) trigger.disabled = true;

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    // Dispatched before the request so the cart drawer can open optimistically;
    // it stays in its loading state until the promise below resolves.
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: this.#cartSectionIds() }),
      }).then((res) => res.json());

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        this.#showError(response.description || response.message || '');

        const cart = await this.#refreshCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'mmx-product-information', itemCount: totalQuantity },
        });
        return;
      }

      await this.#applyDiscount(tierCode);

      const cart = await this.#refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'mmx-product-information',
          itemCount: totalQuantity,
          sections: response.sections,
        },
      });

      this.#showAdded(trigger);
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError(this.dataset.genericError || '');
    } finally {
      this.#submitting = false;
      trigger?.removeAttribute('aria-busy');
      if (trigger instanceof HTMLButtonElement) trigger.disabled = false;
      this.#updateBuyState();
    }
  }

  /**
   * `/cart/update.js` replaces the whole discount list, so the codes already on
   * the cart have to be sent back alongside the new one.
   *
   * A tier code wins over the block's code, and only a tier code is written to
   * the cart attribute — that attribute is the drawer's licence to remove a
   * code, and the block's code has no line backing it to remove it against.
   *
   * @param {string} [tierCode]
   */
  async #applyDiscount(tierCode) {
    const blockCode = (this.buy instanceof HTMLElement ? this.buy.dataset.discountCode || '' : '').trim();
    const code = tierCode || blockCode;
    if (!code) return;

    const cart = await this.#fetchCartJson();
    const existing = (cart?.discount_codes ?? []).map((discount) => discount.code).filter(Boolean);
    const owned = this.#ownedCodes(cart);

    const isApplied = existing.some((entry) => entry.toLowerCase() === code.toLowerCase());
    const isOwned = !tierCode || owned.some((entry) => entry.toLowerCase() === code.toLowerCase());
    if (isApplied && isOwned) return;

    /** @type {{ discount: string, attributes?: Record<string, string> }} */
    const body = { discount: (isApplied ? existing : [...existing, code]).join(',') };
    if (tierCode && !isOwned) {
      body.attributes = { [TIER_DISCOUNT_ATTRIBUTE]: [...owned, code].join(',') };
    }

    await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /** @param {{ attributes?: Record<string, string> } | null} cart */
  #ownedCodes(cart) {
    return String(cart?.attributes?.[TIER_DISCOUNT_ATTRIBUTE] || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);
  }

  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');
  }

  #fetchCartJson() {
    return fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());
  }

  async #refreshCart() {
    const cartItems = document.querySelector('cart-items-component');

    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }

    return this.#fetchCartJson();
  }

  /* -------------------------------- Feedback ------------------------------- */

  /** @param {Element | null} trigger */
  #showAdded(trigger) {
    trigger?.classList.add('is-added');
    if (this.#addedTimeout) clearTimeout(this.#addedTimeout);
    this.#addedTimeout = setTimeout(() => trigger?.classList.remove('is-added'), 2000);
  }

  /** @param {string} message */
  #showError(message) {
    if (!this.errorNode) return;
    this.errorNode.textContent = message;
    this.errorNode.classList.toggle('is-visible', message !== '');
  }

  #hideError() {
    this.#showError('');
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }
}

if (!customElements.get('mmx-product-information')) {
  customElements.define('mmx-product-information', MmxProductInformation);
}
